import { Size } from "@brightagency/headstart-common";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PaidIcon from '@mui/icons-material/Paid';
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import TuneIcon from '@mui/icons-material/Tune';
import React, { useRef } from "react";
import styled from "styled-components";
import { SortModes, WPTaxTerm } from "../../constants/types";
import { useBreakpoint } from "../../controllers";
import { useLocationAccessDenied, useSortMode, useRegionSelectVisibility, useFreeServiceVisibility, useAssestmentFilterVisibility, useTaxonomies } from "../../services/app-state";
import { useAssessmentState } from "../../services/assessment-state";
import { buttonResetStyles, useSuburbName } from "../../utils";
import { getSuburbSelectUrl } from "../../utils/basic-only";
import { colors } from "../../utils/colors";
import { TextButton, TextButtonGroup } from "../organisms/AssessmentScreens/shared/TextButton";
import { Carousel, usePagedHorizontalScrolling } from "../organisms/Carousel";
import { CarouselSection } from "../organisms/CarouselSection";

interface Props {
    appearance?: "light" | "dark";
    filter?: true | false;
}

export const Wrapper = styled.button<{ color: string, bgcolor: string }>`
    ${buttonResetStyles};
    display: inline-flex;
    gap: 4px;
    width: auto;
    align-items: center;
    color: ${({ color }) => color};
    font-weight: 500;
    padding: 8px 15px;
    background: ${({ bgcolor }) => bgcolor};
    border-radius: 9999em; 

    &.active{
        background: ${({ color }) => color};
        color: ${({ bgcolor }) => bgcolor};
    }

    @media only screen and (max-width: 640px) {
        min-width: fit-content;
        text-align: center;
    }
`;

export const FilterContainer = styled.div`
    display: inline-block;
    position: relative;
`;

export const FilterListing = styled.div`
    position: absolute;
    max-width: 400px;
    width: 100%;
    z-index: 50;
    background: white;
    padding: 20px;
    border-radius: 10px;
    text-align: center;
    margin-top: 20px;

    h3{
        margin-bottom: 30px;
    }
    
    button{
        width: 100%;
        background-color: rgba(52,99,244,0.1);
        color: ${colors.brandMid};
        flex-basis: 100%;
    }

    @media only screen and (max-width: 640px) {
        max-width: 320px;
    }
`;

export const ResetFilter = styled.a`
    text-decoration: underline;
    color: #2a317d;
    margin-top: 10px;
    display: block;
    cursor: pointer;
`;

export const SortButton: React.FC<Props> = ({ appearance = "dark", filter = false }) => {
    const suburbName = useSuburbName();
    const [sortMode, { setSortMode }] = useSortMode();
    const [locationAccessDenied] = useLocationAccessDenied();
    const color = appearance == "dark" ? colors.brandMid : colors.white;
    const bgcolor = appearance == "dark" ? '#eff3fe' : '#5970f5';

    const [, { setRegionSelectVisibility }] = useRegionSelectVisibility();
    const [showingFreeServices, { setFreeServiceVisibility }] = useFreeServiceVisibility();
    const [showingAssetmentFilter, { setAssetmentFilterVisibility }] = useAssestmentFilterVisibility();
    
    const { state, setState } = useAssessmentState();
    const [{ communities }] = useTaxonomies();
    const selectedCommunities = communities.filter(community => state.communities?.indexOf(community.id.toString()) !== -1);
    const active_community_object = communities.find(community => (state.active_community == community.id.toString()));

    const largeBreakpoint = useBreakpoint(Size.large, 50);
    const trackRef = useRef<HTMLDivElement>(null);
    const itemWidth = 150;
    const itemGutter = largeBreakpoint ? 16 : 8;
    const scrollAmount = itemWidth + itemGutter;

    const { scrollLeft, scrollRight, canScroll } = usePagedHorizontalScrolling({
        trackRef,
        pageWidth: scrollAmount,
        contentCount: 4,
    });

    const handleResetFilter = () => {
        setState({
            active_community: undefined
        });
        setAssetmentFilterVisibility(false);
    }

    const handleAnchorClick = () => {
        setRegionSelectVisibility(true);
    };

    const handleFilterVisibility = () => {
        let display = false;
        if (!showingAssetmentFilter) {
            display = true;
        }
        setAssetmentFilterVisibility(display);
    };

    const handleFilterSelection = (community: WPTaxTerm) => {
        const { active_community } = state;
        if (active_community == community.id?.toString()) {
            setState({
                active_community: undefined
            });
        } else  {
            setState({
                active_community: community.id?.toString()
            });
        }
        setAssetmentFilterVisibility(false);
    };

    const handleFreeServiceVisibility = () => {
        let display = false;
        if (!showingFreeServices) {
            display = true;
        }
        setFreeServiceVisibility(display);
    };

    if (suburbName == null) {
        return (
            <Wrapper
                color={color}
                bgcolor={bgcolor}
                onClick={() => {
                    window.location.href = getSuburbSelectUrl();
                }}
            >
                <LocationOnRoundedIcon />
                Set suburb to show distance
            </Wrapper>
        );
    }
    
    if (locationAccessDenied) {
        return null;
    }

    return (
    <>
    <CarouselSection
        id='sortButtons'
        title=''
        carousel={{
            onScrollLeft: scrollLeft,
            onScrollRight: scrollRight,
            canScroll,
            displayControl: false,
            content: (
            <Carousel scrollingRef={trackRef} itemWidth={itemWidth} itemGutter={itemGutter}>
                {filter && selectedCommunities.length ? (
                    <Wrapper
                        color={color}
                        bgcolor={active_community_object ? '#1D2257' : bgcolor}
                        onClick={handleFilterVisibility}
                        className={showingAssetmentFilter ? "active" : ""}
                    >
                        <TuneIcon />
                        Filter{ active_community_object ? ': ' + active_community_object?.name : '' }
                    </Wrapper>     
                ): ''}
                <Wrapper
                    color={color}
                    bgcolor={bgcolor}
                    onClick={handleFreeServiceVisibility}
                    className={showingFreeServices ? "active": ""} 
                >
                    <PaidIcon />
                    Free services
                </Wrapper>
                
                <Wrapper
                    color={color}
                    bgcolor={bgcolor}
                    onClick={() => {
                        setSortMode(
                            sortMode === SortModes.DistanceFromSuburb
                                ? SortModes.DistanceFromUser
                                : SortModes.DistanceFromSuburb,
                        );
                    }}
                    className={sortMode === SortModes.DistanceFromUser ? "active": ""} 
                >
                    <SortRoundedIcon />
                    Show distance from me
                </Wrapper>
                    
                <Wrapper
                    color={color}
                    bgcolor={bgcolor}
                    onClick={handleAnchorClick}
                >
                    <LocationOnRoundedIcon />
                    Change suburb
                </Wrapper>
            </Carousel> 
            ),
        }}
    />
    {showingAssetmentFilter ? (
        <FilterListing>
            <h3>Show me services that provide support specifically for</h3>
            <TextButtonGroup>
                {selectedCommunities?.map((community) => {     
                    return (
                        <TextButton
                            key={community.id}
                            onClick={() => handleFilterSelection(community)}
                            isActive={state.active_community == community.id.toString()}
                        >
                            {community.acf?.nswtb_process_title}
                        </TextButton>
                    )
                })}
            </TextButtonGroup>
                <ResetFilter
                    onClick={() => handleResetFilter()}
                    >Reset Filter
                </ResetFilter>
        </FilterListing>
    ) : ''}  
    </>
    );
};
